import { motion, useReducedMotion } from 'motion/react';
import { formatPercent } from '../OddsPanel/format';
import {
  BAR_FILL_RADIUS,
  BAR_NAME_COLOR,
  BAR_PERCENT_COLOR,
  BAR_RADIUS,
  BAR_RANGE_COLOR,
  BAR_TRACK,
} from '../OddsBreakdown';
import { tierBarFillStyle } from './tierBar';

export interface OddsBarProps {
  /** Rarity name — drives colour + left label (e.g. "Common"). */
  name: string;
  /** Value range for the tier, pre-formatted by the backend (e.g. "$30 - $60"). */
  range: string;
  /** Drop chance as a percent (0–100). Drives the fill width + right label. */
  percentage: number;
}

/** Narrowest fill so a sub-1% tier is still a visible sliver, not a hairline. */
const MIN_FILL = '18px';

/**
 * Single rarity row inside the odds breakdown — Figma `1578:1471`. A 39px pill
 * track with a near-square rarity fill that sits slightly taller than the track
 * (clipped top/bottom so it reads as a flat bar); width ∝ drop chance (clamped
 * to a minimum so rare tiers stay visible). Rarity name + value range on one
 * line, percentage on the right.
 *
 * The fill springs to its new width when the volatility level changes
 * (Normal/High/Max); honours `prefers-reduced-motion`. Track / text / radius are
 * themed via the `OddsBreakdown` design system (`--co-OddsBreakdown-*`); the
 * rarity fill colour stays on the rarity palette (`--ui-badge-*`, via {@link tierBarFillStyle}).
 */
export function OddsBar({ name, range, percentage }: OddsBarProps) {
  const pct = Math.max(0, Math.min(100, percentage));
  // A 0% tier (e.g. a middle tier hollowed out on Max) gets no fill at all —
  // the min-width sliver applies only to tiers with a real, non-zero chance.
  const hasFill = pct > 0;
  const reduceMotion = useReducedMotion();

  return (
    <div
      className="relative flex h-[39px] items-center overflow-hidden px-3"
      style={{ borderRadius: BAR_RADIUS }}
    >
      {/* Base track. */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{ background: BAR_TRACK, borderRadius: BAR_RADIUS }}
      />

      {/* Rarity-coloured fill — width ∝ drop chance; springs on volatility switch.
          The colour lives on a nested plain div: spreading React.CSSProperties into
          motion's stricter MotionStyle breaks the build, so the animated wrapper
          keeps only motion-safe keys and clips the fill via overflow-hidden. */}
      <motion.div
        aria-hidden
        className="absolute left-0 overflow-hidden"
        style={{
          // Sits taller than the 39px track and vertically centred; the row's
          // overflow-hidden clips top/bottom so it reads as a flat, near-square bar.
          top: '50%',
          marginTop: '-27px',
          height: '54px',
          minWidth: hasFill ? MIN_FILL : 0,
          borderRadius: BAR_FILL_RADIUS,
        }}
        initial={false}
        animate={{ width: `${pct}%` }}
        transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 200, damping: 30 }}
      >
        <div className="absolute inset-0" style={tierBarFillStyle(name)} />
      </motion.div>

      {/* Content over the fill. */}
      <div className="relative flex flex-1 items-center justify-between gap-3">
        <div className="relative flex-1 text-[13px] leading-[20px]">
          <span className="font-semibold" style={{ color: BAR_NAME_COLOR }}>
            {name}
          </span>
          <span
            className="absolute left-[88px] top-0 whitespace-nowrap font-normal tracking-[-0.1px]"
            style={{ color: BAR_RANGE_COLOR }}
          >
            {range}
          </span>
        </div>
        <span
          className="whitespace-nowrap text-[15px] font-semibold leading-[22px] tracking-[-0.3px]"
          style={{ color: BAR_PERCENT_COLOR }}
        >
          {formatPercent(percentage)}%
        </span>
      </div>
    </div>
  );
}
